import { facilityAddonIconId } from './assetFacilityIcons';
import {
  catalogRowDisplayName,
  enrichFacilitiesWithCatalog,
  facilityCatalogRowId,
  mergeFacilitiesAndPromotedAddonsForDisplay,
} from './assetPromotedFacilities';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function pushFacilitySource(into: unknown[], raw: unknown): void {
  if (raw == null) {
    return;
  }
  if (Array.isArray(raw)) {
    into.push(...raw);
    return;
  }
  into.push(raw);
}

function collectAssetFacilityRows(asset: Record<string, unknown>): unknown[] {
  const rows: unknown[] = [];
  pushFacilitySource(rows, asset.facilities);
  pushFacilitySource(rows, asset.facility_ids);
  pushFacilitySource(rows, asset.facilityIds);
  if (isPlainObject(asset.facility)) {
    pushFacilitySource(rows, asset.facility.facilities);
    pushFacilitySource(rows, asset.facility.facility_ids);
  }
  if (isPlainObject(asset.asset)) {
    pushFacilitySource(rows, asset.asset.facilities);
    pushFacilitySource(rows, asset.asset.facility_ids);
  }
  return rows;
}

/** True when the list/detail row already carries facility ids or names. */
export function assetRecordHasFacilitySnapshot(asset: Record<string, unknown>): boolean {
  return collectAssetFacilityRows(asset).length > 0;
}

/** True when a catalog/listing facility label is the 24/7 access amenity. */
export function is24x7FacilityLabel(label: string): boolean {
  return facilityAddonIconId(label) === 'access24';
}

export function facilityCatalog24x7Ids(catalog: Record<string, unknown>[]): number[] {
  const ids: number[] = [];
  for (const row of catalog) {
    if (!is24x7FacilityLabel(catalogRowDisplayName(row))) {
      continue;
    }
    const id = facilityCatalogRowId(row);
    if (id !== null) {
      ids.push(id);
    }
  }
  return ids;
}

/** True when the listing has the 24/7 access facility attribute. */
export function assetRecordHas24x7Access(
  asset: Record<string, unknown>,
  facilityCatalog: Record<string, unknown>[] = [],
  known24x7Ids: ReadonlySet<number> | number[] = [],
): boolean {
  const idSet = known24x7Ids instanceof Set ? known24x7Ids : new Set(known24x7Ids);
  const merged = mergeFacilitiesAndPromotedAddonsForDisplay(
    collectAssetFacilityRows(asset),
    Array.isArray(asset.addons) ? asset.addons : undefined,
  );
  const enriched =
    facilityCatalog.length > 0 ? enrichFacilitiesWithCatalog(merged, facilityCatalog) : merged;
  for (const row of enriched) {
    const id = facilityCatalogRowId(row);
    if (id !== null && idSet.has(id)) {
      return true;
    }
    if (is24x7FacilityLabel(catalogRowDisplayName(row))) {
      return true;
    }
  }
  return false;
}

/** Keep only listings with the 24/7 access attribute. Empty when none have it. */
export function refineAssetsForOpen24hSearch(
  assets: Record<string, unknown>[],
  known24x7Ids: ReadonlySet<number> | number[] = [],
  facilityCatalog: Record<string, unknown>[] = [],
): Record<string, unknown>[] {
  return assets.filter((asset) => assetRecordHas24x7Access(asset, facilityCatalog, known24x7Ids));
}
